/**
 * Block registry (Sprint 3, app side) — maps a Puck block `type` (the
 * component key stored in puckData.content[].type) to the app's render
 * component.
 *
 * The app repo doesn't share the intranet's exact config keys, so each
 * block registers under SEVERAL candidate aliases (EN/FR, with/without a
 * "Block" suffix), matched case-insensitively. Unknown types render the
 * graceful placeholder instead of crashing (see PuckPageRenderer).
 *
 * To add a block: create the component in ./blocks, then register it
 * here under every plausible key the intranet might emit.
 */
import HeroBlock from './blocks/HeroBlock';
import BannerBlock from './blocks/BannerBlock';
import EventBlock from './blocks/EventBlock';
import PCCEventBlock from './blocks/PCCEventBlock';
import DonationBlock from './blocks/DonationBlock';
import ReadingPlanBlock from './blocks/ReadingPlanBlock';
import TextBlock from './blocks/TextBlock';
import ImageBlock from './blocks/ImageBlock';
import ButtonBlock from './blocks/ButtonBlock';
import VideoBlock from './blocks/VideoBlock';
import SpacerBlock from './blocks/SpacerBlock';
import CardBlock from './blocks/CardBlock';
import FaqBlock from './blocks/FaqBlock';
import CarouselBlock from './blocks/CarouselBlock';

// key (lowercased, alnum-only) → component
const _REGISTRY = {};

function register(component, aliases) {
  for (const a of aliases) {
    _REGISTRY[normalizeType(a)] = component;
  }
}

/** Normalise a type string: lowercase, strip non-alphanumerics. */
export function normalizeType(t) {
  return String(t || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

register(HeroBlock, ['Hero', 'HeroBlock', 'BlocHero', 'blochero']);
register(BannerBlock, ['Banner', 'BannerBlock', 'Banniere', 'Bannière', 'BlocBanniere']);
register(EventBlock, ['Event', 'EventBlock', 'Evenement', 'Événement', 'BlocEvenement']);
register(PCCEventBlock, ['PCCEvent', 'PCCEventBlock', 'EvenementPCC', 'EventPCC', 'BlocEvenementPCC']);
register(DonationBlock, ['Donation', 'DonationBlock', 'Don', 'BlocDon', 'Donner']);
register(ReadingPlanBlock, ['ReadingPlan', 'ReadingPlanBlock', 'PlanLecture', 'PlanDeLecture', 'BlocPlanLecture', 'YouVersion']);
// Blocs génériques (set Essentiel).
register(TextBlock, ['Text', 'TextBlock', 'Texte', 'BlocTexte', 'RichText']);
register(ImageBlock, ['Image', 'ImageBlock', 'BlocImage', 'Photo']);
register(ButtonBlock, ['Button', 'ButtonBlock', 'Bouton', 'BlocBouton', 'CTA']);
register(VideoBlock, ['Video', 'VideoBlock', 'Vidéo', 'BlocVideo', 'YouTube']);
register(SpacerBlock, ['Spacer', 'SpacerBlock', 'Separateur', 'Séparateur', 'BlocSeparateur', 'Espace', 'Divider']);
register(CardBlock, ['Card', 'CardBlock', 'Carte', 'BlocCarte', 'Tuile']);
register(FaqBlock, ['Faq', 'FaqBlock', 'FAQ', 'Accordeon', 'Accordéon', 'Accordion', 'BlocFaq']);
register(CarouselBlock, ['Carousel', 'CarouselBlock', 'Carrousel', 'BlocCarrousel', 'Slider', 'Diaporama']);

/** Resolve a Puck block type to a component, or null if unknown. */
export function resolveBlock(type) {
  return _REGISTRY[normalizeType(type)] || null;
}
